'use client';

import useSWR, { mutate } from 'swr';
import { fleetApi } from '../api/axiosClient';
import FilterBuilder from '../utils/filterBuilder';
import { useMemo } from 'react';
import { normalizeApiResponse } from '../utils/response';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidatePolygonTypeList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/polygon-type/search');
};

export const usePolygonTypeList = (page, rowsPerPage, searchKey, searchValue) => {
  const start = page * rowsPerPage;
  const payload = useMemo(() => {
    const fb = new FilterBuilder().setPaging(start, rowsPerPage).setOrder(['name'], true);

    if (searchValue && searchValue.trim() !== '') {
      fb.like(searchKey || 'name', searchValue, 'AND');
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue]);

  const key = useMemo(() => ['/polygon-type/search', JSON.stringify(payload)], [payload]);
  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  return normalizeApiResponse(data, error, isLoading);
};

export const getPolygonTypeById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.get(`/polygon-type/${id}${getTzParam()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch polygon type by ID:', error);
    throw error;
  }
};

export const createPolygonType = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/polygon-type/new${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidatePolygonTypeList();
    return response.data;
  } catch (error) {
    console.error('Error creating polygon type:', error);
    throw error;
  }
};

export const updatePolygonType = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');

  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.put(`/polygon-type/edit/${id}${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidatePolygonTypeList();
    mutate(`/polygon-type/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error updating polygon type:', error);
    throw error;
  }
};

export const deletePolygonType = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.delete(`/polygon-type/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidatePolygonTypeList();
    return response.data;
  } catch (error) {
    console.error('Error deleting polygon type:', error);
    throw error;
  }
};
